import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Table, Button, Input, Space, Tag, Popconfirm, message } from "antd";
import { useNavigate } from "react-router-dom";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Constants from "../Utils/Constants";


const api = axios.create({
  baseURL: Constants.SERVER_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function ProgramMenu() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  async function loadPatients() {
    setLoading(true);

    try {
      const { data } = await api.get("evaluations/pace-patients");
      setList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      message.error("Impossible de charger la liste des patients.");
      setList([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPatients();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();

    if (!term) return list;

    return list.filter((patient) =>
      `${patient.firstname || ""} ${patient.lastname || ""}`
        .toLowerCase()
        .includes(term)
    );
  }, [q, list]);

  async function deleteProgramme(programmeId) {
    try {
      await api.delete(`/programme/programmes/${encodeURIComponent(programmeId)}`);

      message.success("Programme supprimé avec succès.");
      await loadPatients();
    } catch (error) {
      console.error("Erreur suppression programme :", error);
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Impossible de supprimer le programme."
      );
    }
  }

  const columns = [
    {
      title: "Patient",
      key: "name",
      render: (_, record) => `${record.firstname} ${record.lastname}`,
    },
    {
      title: "Dernière évaluation PACE",
      dataIndex: "evaluationDate",
      key: "evaluationDate",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("fr-FR") : "—",
    },
    {
      title: "Programme",
      key: "programme",
      render: (_, record) =>
        record.hasProgramme ? (
          <Tag color="green">Assigné</Tag>
        ) : (
          <Tag>En attente</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const patientName = `${record.firstname} ${record.lastname}`;

        if (!record.hasProgramme) {
          return (
            <Button
              type="primary"
              onClick={() =>
                navigate(`/programme2/assign/${record.patientId}`, {
                  state: { patientName },
                })
              }
            >
              Assigner
            </Button>
          );
        }

        return (
          <Space wrap>
            <Button
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(`/programme2/${record.programmeId}`, {
                  state: { patientName },
                })
              }
            >
              Voir
            </Button>

            <Button
              icon={<EditOutlined />}
              onClick={() =>
                navigate(`/programme2/${record.programmeId}/edit`, {
                  state: { patientName },
                })
              }
            >
              Modifier
            </Button>

            <Popconfirm
              title="Supprimer ce programme ?"
              description="Cette action va retirer le programme assigné à ce patient."
              okText="Supprimer"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteProgramme(record.programmeId)}
            >
              <Button danger icon={<DeleteOutlined />}>
                Supprimer
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2>Liste des patients</h2>

      <Space style={{ marginBottom: 12 }}>
        <Input
          placeholder="Rechercher par nom/prénom"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          allowClear
          style={{ width: 260 }}
        />
      </Space>

      <Table
        rowKey={(record) => `${record.patientId}-${record.evaluationId}`}
        loading={loading}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
}